import React, { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { default as ARCLightButton } from "components/ARC/LightButton";
import FlashButton from "components/ARC/FlashButton";
import SpeakerButton from "components/control/SpeakerButton";
import { getLiveAudioWsUrlWithToken } from "lib/consts/liveAudio";
import { getAuthToken } from "lib/utils/auth";
import { useARC } from "providers/ARC/ARCContext";
import useFleetArcId from "lib/utils/useFleetArcId";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import CameraButton from "components/inspections/CameraButton";
import DeviationDetectionButton from "components/inspections/DeviationDetectionButton";
import { default as MissionProgressBar } from "components/navigation/mission/ProgressBar";
import ObjectDetectionButton from "components/inspections/ObjectDetectionButton";
import ShowControlButton from "./ShowControlButton";
import { useDeviceFeatureFlags } from "providers/DeviceFeatureFlagsProvider";
import ControlGimbalButton from "./ControlGimbalButton";
import ControlInsta360Button from "./ControlInsta360Button";
import ControlArmButton from "./ControlArmButton";
import ShowLogHistoryButton from "components/navigation/mission/ShowLogHistoryButton";
import SwitchScreenButtons from "components/control/SwitchScreenButtons";
import { default as RobotModeBar } from "components/robot/ModeBar";
import TakeReadingButton from "components/inspections/TakeReadingButton";
import FunctionalButton from "components/layout/FunctionalButton";
import StairsButton from "components/control/StairsButton";
import DockingButton from "components/control/DockingButton";
import { useScreen } from "layout/ControlLayout";
import { ReactComponent as RecordIcon } from "assets/svgs/navbar/record.svg";
import { ReactComponent as PhotoGalleryIcon } from "assets/svgs/navbar/photogallery.svg";
import RecordingsPopup from "./RecordingsPopup";
import { useLightControl } from "providers/ARC/LightControlProvider";


const BottomBar = () => {
    const { t } = useTranslation("control");
    const navigate = useNavigate();
    const location = useLocation();
    const [recordingsOpen, setRecordingsOpen] = useState(false);
    const { setShowStairsDialog } = useScreen();
    const { selectedARC } = useARC();
    const { isSecurityModuleConnected } = useLightControl();
    const { flags } = useDeviceFeatureFlags();
    const { HAS_MISSIONS, HAS_NAV_AUTOMATION } = flags;
    const arcId = useFleetArcId();
    const isSecure = useIsHttpSecure();
    const authToken = getAuthToken();
    const liveAudioWsUrl = useMemo(
        () => getLiveAudioWsUrlWithToken(authToken, selectedARC?.host, isSecure, arcId),
        [authToken, selectedARC?.host, isSecure, arcId],
    );
    const galleryActive = /\/gallery\/?$/.test(location.pathname);

    return (
        <div className="flex min-h-[3.75rem] w-full min-w-0 items-center overflow-x-auto overflow-y-hidden overscroll-x-contain bg-chrome px-5 text-white">
            {/*
              Single scroll row: ModeBar is in flex flow (not absolute) so it cannot
              overlap side controls on narrow Fold / high-DPI layouts. w-max + min-w-full
              keeps left/mode/right spread on wide screens and scrolls when content overflows.
            */}
            <div className="flex w-max min-w-full items-center justify-between gap-x-4">
                <div className="flex shrink-0 items-center justify-start gap-x-4">
                    <SwitchScreenButtons />
                    <ARCLightButton />
                    {isSecurityModuleConnected && <FlashButton />}
                    {HAS_NAV_AUTOMATION && <StairsButton onClick={() => setShowStairsDialog(true)} />}
                    {HAS_NAV_AUTOMATION && <DockingButton />}
                    <ShowControlButton />
                    <ControlGimbalButton />
                    <ControlInsta360Button />
                    <ControlArmButton />
                    {false && (
                        <>
                            <CameraButton />
                            <ObjectDetectionButton />
                            <DeviationDetectionButton />
                            <TakeReadingButton />
                        </>
                    )}
                </div>
                <div className="flex shrink-0 items-center justify-center">
                    <RobotModeBar />
                </div>
                <div className="flex shrink-0 items-center justify-end gap-x-4">
                    <SpeakerButton wsUrl={liveAudioWsUrl} />
                    <FunctionalButton
                        className={twMerge(
                            "control-button-type3 shrink-0 text-white transition-all duration-ios-open ease-ios",
                            recordingsOpen && "scale-95 bg-primary2 text-on-accent"
                        )}
                        icon={<RecordIcon className="h-5 w-5 shrink-0" />}
                        hoverText={t("bottomBar.record")}
                        onClick={() => setRecordingsOpen((open) => !open)}
                    />
                    <RecordingsPopup
                        visible={recordingsOpen}
                        onClose={() => setRecordingsOpen(false)}
                    />
                    <FunctionalButton
                        className={twMerge(
                            "control-button-type3 shrink-0 text-white transition-all duration-ios-open ease-ios",
                            galleryActive && "scale-95 bg-primary2 text-on-accent"
                        )}
                        icon={<PhotoGalleryIcon className="h-5 w-5 shrink-0" />}
                        hoverText={t("bottomBar.gallery")}
                        onClick={() => navigate("gallery")}
                    />
                    {HAS_MISSIONS && <MissionProgressBar />}
                    {false && <ShowLogHistoryButton />}
                </div>
            </div>
        </div>
    );
};

export default BottomBar;
